import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { AIReview } from "common/models/types";
import AIReviewManagementTemplate from "components/templates/AIReviewManagementTemplate";
import { useAIReviewList } from "hooks/useAIReviewList";
import { useAIReviewPin } from "hooks/useAIReviewPin";
import { eventLog } from "utils/AmplitudeUtil";
import { log, LogLevel } from "utils/LogUtil";

type TabType = "pinned" | "recent";

const PAGE_SIZE = 20; // 고정. 추후 변경 UI 추가 시 localStorage 연계 예정 (이번 스프린트 제외)

const resolveInitialTab = (search: string): TabType => {
  const value = new URLSearchParams(search).get("tab");
  return value === "recent" ? "recent" : "pinned";
};

const AIReviewsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const patients = useSelector((state: RootState) => state.data.patients);

  // URL은 "최초 진입 시점의 초기 탭"으로만 사용. 이후 탭/페이지 전환은 내부 state만 변경.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialTab = useMemo(() => resolveInitialTab(location.search), []);
  const [tab, setTab] = useState<TabType>(initialTab);
  const [page, setPage] = useState<number>(1);

  const { listQuery, countQuery, pinnedCount, recentCount } = useAIReviewList({
    tab,
    page,
    pageSize: PAGE_SIZE,
  });
  const { togglePin, isLoading: isPinLoading } = useAIReviewPin();

  useEffect(() => {
    eventLog("보고서관리_페이지_진입", { initialTab });
    log(LogLevel.VERBOSE, "AIReviewsPage: mounted", { initialTab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (next: TabType) => {
    if (next === tab) return;
    eventLog("보고서관리_탭_변경", { tab: next });
    setTab(next);
    setPage(1);
  };

  const handlePageChange = (next: number) => {
    eventLog("보고서관리_페이지_변경", { tab, page: next });
    setPage(next);
  };

  const handleReportClick = (review: AIReview) => {
    eventLog("보고서관리_보고서_선택", { reviewId: review.id, patientId: review.patientId });
    if (review.patientId && review.id) {
      navigate(`/patients/${review.patientId}/reviews/${review.id}`);
    }
  };

  return (
    <AIReviewManagementTemplate
      tab={tab}
      page={page}
      pageSize={PAGE_SIZE}
      total={countQuery.data ?? 0}
      pinnedCount={pinnedCount}
      recentCount={recentCount}
      items={listQuery.data ?? []}
      patients={patients}
      isLoading={listQuery.isLoading}
      isError={listQuery.isError || countQuery.isError}
      isPinLoading={isPinLoading}
      onTabChange={handleTabChange}
      onPageChange={handlePageChange}
      onReportClick={handleReportClick}
      onTogglePin={togglePin}
    />
  );
};

export default AIReviewsPage;
